import { authService } from "./authService";

// Define the WebSocket message types
interface RecordingEventMessage {
  type: "recording_event";
  recording_id: string;
  event_type: string;
  progress: number;
  service: string;
  user_id: number;
  timestamp: string;
  status: string | null;
  metadata: {
    environment: string;
    event_id: string;
    file_path: string | null;
    transcription: any;
    processing_time: number | null;
    text_length: number | null;
    template_id: string | null;
    patient_name: string | null;
    icd_codes_count: number | null;
    cpt_codes_count: number | null;
  };
}

type WebSocketMessage = RecordingEventMessage | any;

// Event callback type
type RecordingEventCallback = (event: RecordingEventMessage) => void;

class WebSocketManager {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 3000;
  private isConnecting = false;
  private isDestroyed = false;
  private listeners: Set<RecordingEventCallback> = new Set();
  private connectionPromise: Promise<boolean> | null = null;

  /**
   * Connect to WebSocket server (singleton connection)
   */
  async connect(): Promise<boolean> {
    // Return existing connection promise if already connecting
    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    // If already connected, return true
    if (this.isConnected()) {
      return true;
    }

    // Create new connection promise
    this.connectionPromise = this.createConnection();
    const result = await this.connectionPromise;
    this.connectionPromise = null;
    return result;
  }

  /**
   * Create WebSocket connection
   */
  private async createConnection(): Promise<boolean> {
    if (this.isDestroyed) {
      console.log("❌ WebSocket manager is destroyed, cannot connect");
      return false;
    }

    this.isConnecting = true;

    try {
      const token = authService.getToken();
      if (!token) {
        console.error("❌ No auth token available for WebSocket");
        this.isConnecting = false;
        return false;
      }

      console.log("🔌 Connecting to WebSocket...");
      
      // Connect to backend WebSocket using environment variable
      const socketHost = process.env.REACT_APP_SOCKET_URL;
      // choose secure socket if page is https or the socket host looks like an https URL
      const useSecure = (typeof window !== 'undefined' && window.location && window.location.protocol === 'https:') || (socketHost && socketHost.startsWith('https'));
      const scheme = useSecure ? 'wss' : 'ws';
      const wsUrl = `${scheme}://${socketHost}/api/messagebroker?token=${token}`;

      // Log connection attempt with masked token (don't print the actual token)
      const masked = wsUrl.replace(/(token=)[^&]+/, '$1***');
      console.log(`🔌 Attempting WebSocket connection to: ${masked}`);

      return new Promise((resolve) => {
        // construct WebSocket inside try/catch to handle synchronous constructor errors
        try {
          this.ws = new WebSocket(wsUrl);
        } catch (ctorError) {
          console.error('❌ WebSocket constructor failed synchronously:', ctorError);
          this.isConnecting = false;
          resolve(false);
          return;
        }

        const timeout = setTimeout(() => {
          console.error("❌ WebSocket connection timeout");
          this.ws?.close();
          this.isConnecting = false;
          resolve(false);
        }, 10000);

        this.ws.onopen = () => {
          clearTimeout(timeout);
          console.log("✅ WebSocket connected successfully");
          this.reconnectAttempts = 0;
          this.isConnecting = false;
          resolve(true);
        };

        this.ws.onmessage = (event) => {
          this.handleMessage(event.data);
        };

        this.ws.onclose = (event) => {
          clearTimeout(timeout);
          console.log(`🔌 WebSocket closed: ${event.code} ${event.reason}`);
          this.ws = null;
          this.isConnecting = false;

          // Auto-reconnect if not a clean close and not destroyed
          if (event.code !== 1000 && !this.isDestroyed && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.scheduleReconnect();
          }

          if (this.isConnecting) {
            resolve(false);
          }
        };

        this.ws.onerror = (error) => {
          clearTimeout(timeout);
          console.error("❌ WebSocket error:", error);
          
          // Check if this might be an SSL certificate error
          this.handleSSLCertificateError(socketHost);
          
          this.isConnecting = false;
          resolve(false);
        };
      });

    } catch (error) {
      console.error("❌ Failed to create WebSocket:", error);
      this.isConnecting = false;
      return false;
    }
  }

  /**
   * Handle incoming WebSocket messages
   */
  private handleMessage(data: string): void {
    try {
      const message: WebSocketMessage = JSON.parse(data);
      
      // Log all messages
      console.log("📨 WebSocket message:", message);

      // Handle recording events specifically
      if (message.type === "recording_event") {
        const event = message as RecordingEventMessage;
        
        console.log(`🎬 Recording Event: ${event.event_type}`);
        console.log(`   Recording ID: ${event.recording_id}`);
        console.log(`   Progress: ${event.progress}%`);
        console.log(`   Status: ${event.status || 'N/A'}`);
        console.log(`   Service: ${event.service}`);

        // Notify all listeners
        this.listeners.forEach(callback => {
          try {
            callback(event);
          } catch (error) {
            console.error("❌ Error in WebSocket listener:", error);
          }
        });
      }

    } catch (error) {
      console.log("📨 Non-JSON WebSocket message:", data);
    }
  }

  /**
   * Handle SSL certificate error by providing user guidance
   */
  private handleSSLCertificateError(socketHost: string | undefined): void {
    if (!socketHost) return;
    
    console.error("💡 SSL Certificate Issue Detected!");
    console.error("🔧 To fix this, you need to accept the SSL certificate:");
    console.error(`   1. Open a new tab and navigate to: https://${socketHost}`);
    console.error("   2. Click 'Advanced' then 'Proceed to site'");
    console.error("   3. Come back and refresh this page");
    console.error("   4. The WebSocket connection should work after accepting the certificate");
    
    // Try to programmatically open the certificate acceptance page
    if (typeof window !== 'undefined') {
      console.log(`🌐 Opening certificate page: https://${socketHost}`);
      // Note: This might be blocked by popup blockers
      try {
        const newWindow = window.open(`https://${socketHost}`, '_blank');
        if (!newWindow) {
          console.warn("⚠️ Popup blocked. Please manually navigate to the URL above.");
        }
      } catch (e) {
        console.warn("⚠️ Could not open certificate page automatically");
      }
    }
  }

  /**
   * Schedule reconnection
   */
  private scheduleReconnect(): void {
    this.reconnectAttempts++;
    const delay = this.reconnectDelay * this.reconnectAttempts;
    
    console.log(`🔄 Scheduling reconnect ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${delay}ms`);
    
    setTimeout(() => {
      if (!this.isDestroyed && this.reconnectAttempts <= this.maxReconnectAttempts) {
        this.connect();
      }
    }, delay);
  }

  /**
   * Add event listener
   */
  addListener(callback: RecordingEventCallback): () => void {
    this.listeners.add(callback);
    
    // Return unsubscribe function
    return () => {
      this.listeners.delete(callback);
    };
  }

  /**
   * Send message
   */
  send(message: any): void {
    if (!this.isConnected()) {
      console.warn("⚠️ Cannot send message - WebSocket not connected");
      return;
    }

    const data = typeof message === 'string' ? message : JSON.stringify(message);
    this.ws!.send(data);
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * Test SSL certificate acceptance by making a simple HTTPS request
   */
  async testSSLCertificate(): Promise<boolean> {
    const socketHost = process.env.REACT_APP_SOCKET_URL;
    if (!socketHost) return false;

    try {
      await fetch(`https://${socketHost}`, { 
        method: 'HEAD',
        mode: 'no-cors' // This allows the request even if CORS is not configured
      });
      console.log("✅ SSL certificate is accepted by browser");
      return true;
    } catch (error) {
      console.log("❌ SSL certificate not accepted yet:", error);
      return false;
    }
  }

  /**
   * Get connection state
   */
  getState(): string {
    if (!this.ws) return "DISCONNECTED";
    
    switch (this.ws.readyState) {
      case WebSocket.CONNECTING: return "CONNECTING";
      case WebSocket.OPEN: return "OPEN";
      case WebSocket.CLOSING: return "CLOSING";
      case WebSocket.CLOSED: return "CLOSED";
      default: return "UNKNOWN";
    }
  }

  /**
   * Disconnect and cleanup
   */
  disconnect(): void {
    console.log("🔌 Disconnecting WebSocket...");
    
    this.isDestroyed = true;
    this.listeners.clear();
    
    if (this.ws) {
      this.ws.close(1000, "Manual disconnect");
      this.ws = null;
    }
  }

  /**
   * Reset for new connection (e.g., after logout/login)
   */
  reset(): void {
    this.disconnect();
    this.isDestroyed = false;
    this.reconnectAttempts = 0;
    this.connectionPromise = null;
  }
}

// Export singleton instance
export const websocketService = new WebSocketManager();

// Export types
export type { RecordingEventMessage, WebSocketMessage, RecordingEventCallback }; 